import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@fullyops/shared/material.module';
import { HideIfNotAllowedDirective } from '@fullyops/shared/directives/hide-if-not-allowed.directive';
import { PermissionType } from '@fullyops/legacy/ui/ui-shared/utils/crm-types';
import { SidenavPortalBridgeService } from '@fullyops/core/sidenav-form-portal-bridge.service';
import { ListedAssetsService } from '@fullyops/domain/assets/listed-service';
import { map } from 'rxjs';
import { AssetFormTranslatePipe } from '../../translations/translate.pipe';
import {
  NEW_EQUIPMENT_SIDEBAR_TOKEN,
  NewEquipmentSidebarComponent,
} from '../new-equipment-sidebar/new-equipment-sidebar.component';
import {
  ASSET_EDIT_PANEL_TOKEN,
  AssetEditPanelComponent,
} from '../asset-edit-panel/asset-edit-panel.component';

/**
 * A client's assets, inactive ones included, as a list like a work order's actions. They're created and edited in
 * side panels, with the asset page's form.
 */
@Component({
  selector: 'fo-company-assets',
  templateUrl: './company-assets.component.html',
  styleUrl: './company-assets.component.scss',
  imports: [
    CommonModule,
    MaterialModule,
    HideIfNotAllowedDirective,
    AssetFormTranslatePipe,
  ],
})
export class CompanyAssetsComponent implements OnInit, OnDestroy {
  private listed = inject(ListedAssetsService);
  private sidebar = inject(SidenavPortalBridgeService);

  @Input({ required: true }) companyId!: string;

  protected createPerms = [PermissionType.CAN_EDIT_CLIENT_EQUIPMENTS];
  private panelOpen = false;

  protected assets$ = this.listed.assets$.pipe(
    map((as) =>
      as
        .filter((a) => a.companyId === this.companyId)
        .sort((a, b) => collator.compare(a.name, b.name)),
    ),
  );

  ngOnInit(): void {
    this.listed.refresh();
  }

  ngOnDestroy(): void {
    if (this.panelOpen) {
      this.sidebar.closePortal();
    }
    this.listed.clear();
  }

  private close = () => {
    this.panelOpen = false;
    this.sidebar.closePortal();
  };

  private closeAndRefresh = () => {
    this.close();
    this.listed.refresh();
  };

  protected onAdd(): void {
    this.sidebar.openPortal(
      NewEquipmentSidebarComponent,
      NEW_EQUIPMENT_SIDEBAR_TOKEN,
      {
        companyId: this.companyId,
        lockCompany: true,
        actionType: null,
        nested: false,
        onSave: this.closeAndRefresh,
        onCancel: this.close,
      },
      { width: 'large' },
    );
    this.panelOpen = true;
  }

  protected onOpen(assetId: string): void {
    this.sidebar.openPortal(
      AssetEditPanelComponent,
      ASSET_EDIT_PANEL_TOKEN,
      { assetId, onDone: this.closeAndRefresh, onCancel: this.close },
      { width: 'large' },
    );
    this.panelOpen = true;
  }
}

const collator = new Intl.Collator(undefined, { sensitivity: 'accent' });
